import React from 'react';
import { motion } from 'framer-motion';
import { FaHandshake, FaGlobeAmericas, FaChartLine, FaShieldAlt } from 'react-icons/fa';
import SectionHeading from '../ui/SectionHeading';
import ScrollReveal from '../ui/ScrollReveal';
import StatsCard from '../ui/StatsCard';

const AboutSection = () => {
  const stats = [
    { value: 15, label: 'Years Experience' },
    { value: 250, label: 'Trading Partners' },
    { value: 50, label: 'Countries Served' },
    { value: 4, suffix: 'M', label: 'Barrels Traded Monthly' },
  ];

  const values = [
    {
      icon: <FaHandshake className="w-7 h-7 text-primary-400" />,
      title: 'Trust & Reliability',
      description: 'Building lasting relationships through consistent performance and transparent dealings.'
    },
    {
      icon: <FaGlobeAmericas className="w-7 h-7 text-primary-400" />,
      title: 'Global Perspective',
      description: 'Leveraging international market insights to create strategic advantages for our clients.'
    },
    {
      icon: <FaChartLine className="w-7 h-7 text-primary-400" />,
      title: 'Market Expertise',
      description: 'Deep understanding of energy markets to navigate complexities and identify opportunities.'
    },
    {
      icon: <FaShieldAlt className="w-7 h-7 text-primary-400" />,
      title: 'Compliance & Ethics',
      description: 'Unwavering commitment to regulatory standards and ethical business practices.'
    }
  ];

  return (
    <section id="about" className="section-padding relative overflow-hidden bg-black">
      {/* Background gradient */}
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[800px] h-[800px] rounded-full bg-primary-500/10 blur-[100px] pointer-events-none"></div>
      
      <div className="container-custom relative z-10">
        <SectionHeading
          subtitle="About Caishen Capital"
          title="Excellence in Hydrocarbon Trading"
          description="Founded with a vision to transform the global fuel trading landscape, Caishen Capital delivers exceptional value through strategic partnerships, market expertise, and unwavering integrity."
        />
        
        <div className="grid md:grid-cols-2 gap-12 items-center mt-16">
          <div>
            <ScrollReveal>
              <div 
                className="rounded-2xl overflow-hidden relative h-[400px] md:h-[500px] shadow-xl"
                style={{
                  backgroundImage: 'url("https://images.unsplash.com/photo-1669278419307-27bff9b0f3ae")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-850/70 to-transparent"></div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                    className="glass-card p-5 md:p-6"
                  >
                    <blockquote className="text-gray-200 italic">
                      &ldquo;At Caishen Capital, our approach to hydrocarbon trading is built on integrity, 
                      market expertise, and strategic relationships that deliver consistent value 
                      across the global energy supply chain.&rdquo;
                    </blockquote>
                    <div className="mt-3 flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold">
                        RS
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-white">Roger Sels</p>
                        <p className="text-sm text-gray-400">CEO, Caishen Capital Ltd</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </ScrollReveal>
          </div>
          
          <div>
            <ScrollReveal delay={0.1}>
              <h3 className="text-2xl md:text-3xl font-bold mb-5">Our Story</h3>
              <div className="space-y-4 text-gray-300">
                <p>
                  Established in 2009, Caishen Capital emerged from a vision to create a more 
                  efficient, transparent approach to hydrocarbon trading. Named after the Chinese 
                  god of wealth and prosperity, our company embodies the principles of financial 
                  acumen and sustainable growth.
                </p>
                <p>
                  Today, we&apos;ve evolved into a leading global trading house with specialized expertise 
                  in EN590 diesel, crude oil, D6 fuel oil, and Jet A1 fuel. Our global network spans 
                  50+ countries, connecting producers with end-users through strategic partnerships 
                  and innovative trading solutions.
                </p>
                <p>
                  At Caishen Capital, we combine deep market intelligence with operational excellence 
                  to navigate the complex world of energy trading, creating value at every step of the 
                  supply chain.
                </p>
              </div>
            </ScrollReveal>
            
            <div className="mt-8">
              <ScrollReveal delay={0.2}>
                <h3 className="text-xl md:text-2xl font-bold mb-5">Our Core Values</h3>
                <div className="grid grid-cols-2 gap-5">
                  {values.map((value) => (
                    <div key={value.title} className="flex">
                      <div className="mt-1 mr-3">
                        {value.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-1">{value.title}</h4>
                        <p className="text-sm text-gray-300">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
        
        {/* Stats Section */}
        <div className="mt-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                className="glass-card p-6 md:p-8 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2)" }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white font-display flex items-baseline">
                  <motion.span
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      duration: 0.8,
                      delay: 0.2 + (index * 0.1),
                      ease: [0, 0.71, 0.2, 1.01]
                    }}
                    viewport={{ once: true }}
                  >
                    {stat.value}
                  </motion.span>
                  <span className="text-primary-400">{stat.suffix || '+'}</span>
                </div>
                <div className="mt-3 text-gray-300 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;